import { useEffect, useState } from 'react'
import { BaseSensorEntry } from 'types'

interface UseTagFilteringProps {
  tags: BaseSensorEntry[]
}

export const normalizeSearchText = (value: string): string =>
  value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase()

export const matchesAllFilters = (
  value: string,
  filters: string[]
): boolean => {
  if (filters.length === 0) return true

  const normalizedValue = normalizeSearchText(value)
  return filters.every((keyword) =>
    normalizedValue.includes(normalizeSearchText(keyword))
  )
}

export interface UseTagFilteringReturn {
  tagNameSearch: string
  setTagNameSearch: (search: string) => void
  tagDescriptionSearch: string
  setTagDescriptionSearch: (search: string) => void
  tagNameKeywords: string[]
  setTagNameKeywords: (
    keywords: string[] | ((prev: string[]) => string[])
  ) => void
  tagDescriptionKeywords: string[]
  setTagDescriptionKeywords: (
    keywords: string[] | ((prev: string[]) => string[])
  ) => void
  filterMode: 'AND' | 'OR'
  filteredTags: BaseSensorEntry[]
  handleToggleFilterMode: () => void
}

export const useTagFiltering = ({
  tags,
}: UseTagFilteringProps): UseTagFilteringReturn => {
  const [tagNameSearch, setTagNameSearch] = useState('')
  const [tagDescriptionSearch, setTagDescriptionSearch] = useState('')
  const [tagNameKeywords, setTagNameKeywords] = useState<string[]>([])
  const [tagDescriptionKeywords, setTagDescriptionKeywords] = useState<
    string[]
  >([])
  const [filterMode, setFilterMode] = useState<'AND' | 'OR'>('AND')
  const [filteredTags, setFilteredTags] = useState<BaseSensorEntry[]>(tags)

  useEffect(() => {
    setFilteredTags(
      tags.filter((tag) => {
        const allNameFilters = [
          ...tagNameKeywords,
          ...(tagNameSearch.trim() ? [tagNameSearch.trim()] : []),
        ]

        const allDescriptionFilters = [
          ...tagDescriptionKeywords,
          ...(tagDescriptionSearch.trim() ? [tagDescriptionSearch.trim()] : []),
        ]

        // Check if tag name matches all saved keywords or current search input
        const tagName = tag.data?.name ?? ''
        const matchesNameFilters = matchesAllFilters(tagName, allNameFilters)

        // Check if tag description matches all saved keywords or current search input
        const tagDescription = tag.data?.description ?? ''
        const matchesDescriptionFilters = matchesAllFilters(
          tagDescription,
          allDescriptionFilters
        )

        if (filterMode === 'AND') {
          return matchesNameFilters && matchesDescriptionFilters
        } else {
          // OR mode: if no filters are set, show all; otherwise determine if either name or description filters match
          const noFiltersSet =
            allNameFilters.length === 0 && allDescriptionFilters.length === 0
          return (
            noFiltersSet ||
            (allNameFilters.length > 0 && matchesNameFilters) ||
            (allDescriptionFilters.length > 0 && matchesDescriptionFilters)
          )
        }
      })
    )
  }, [
    tagNameKeywords,
    tagDescriptionKeywords,
    tagNameSearch,
    tagDescriptionSearch,
    filterMode,
    tags,
  ])

  const handleToggleFilterMode = () => {
    setFilterMode((prev) => (prev === 'AND' ? 'OR' : 'AND'))
  }

  return {
    tagNameSearch,
    setTagNameSearch,
    tagDescriptionSearch,
    setTagDescriptionSearch,
    tagNameKeywords,
    setTagNameKeywords,
    tagDescriptionKeywords,
    setTagDescriptionKeywords,
    filterMode,
    filteredTags,
    handleToggleFilterMode,
  }
}
